import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { directMessageApi } from '@/services/api/directMessages'
import { useAuthStore } from '@/store/authStore'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { DmListPanel } from '@/components/dm/DmListPanel'
import { DmConversationPanel } from '@/components/dm/DmConversationPanel'
import { NewConversationDialog } from '@/components/dm/NewConversationDialog'
import type { DmConversation } from '@/types/direct-message'

export function MessagesPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useAuthStore(state => state.user)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [filter, setFilter] = useState<'all' | 'unread' | 'archived' | 'teacher'>('all')

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['direct-messages', filter === 'archived'],
    queryFn: () => directMessageApi.getConversations(filter === 'archived'),
    refetchInterval: () => (document.hidden ? false : 10000),
  })

  const conversations = data?.conversations || []
  const listed = conversations.find(c => c.conversation_id === selectedId)
  const { data: selectedFallback, isError: fallbackError, error: fallbackErr } = useQuery({
    queryKey: ['direct-message', selectedId],
    queryFn: () => directMessageApi.getConversation(selectedId!),
    enabled: !!selectedId && !listed,
    retry: false,
  })
  useEffect(() => {
    if (!fallbackError) return
    const code = (fallbackErr as { statusCode?: number } | undefined)?.statusCode
    if (code === 403 || code === 404) {
      setSelectedId(null)
      setSearchParams({}, { replace: true })
    }
  }, [fallbackError, fallbackErr, setSearchParams])
  const selected = listed || selectedFallback || null

  const select = (id: string) => {
    setSelectedId(id)
    setIsMobileOpen(true)
    setSearchParams({ c: id }, { replace: true })
    directMessageApi.markAsRead(id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['direct-messages'] })
        queryClient.invalidateQueries({ queryKey: ['direct-messages-unread-count'] })
      })
      .catch((e: { statusCode?: number }) => {
        if (e.statusCode === 403 || e.statusCode === 404) {
          setSelectedId(null)
          setSearchParams({}, { replace: true })
        }
      })
  }

  useEffect(() => {
    const fromUrl = searchParams.get('c')
    if (fromUrl && fromUrl !== selectedId) {
      setSelectedId(fromUrl)
      setIsMobileOpen(true)
      directMessageApi.markAsRead(fromUrl)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['direct-messages'] })
          queryClient.invalidateQueries({ queryKey: ['direct-messages-unread-count'] })
        })
        .catch((e: { statusCode?: number }) => {
          if (e.statusCode === 403 || e.statusCode === 404) {
            setSelectedId(null)
            setSearchParams({}, { replace: true })
          }
        })
    }
  }, [searchParams, selectedId, queryClient, setSearchParams])

  const onLeft = () => {
    setSelectedId(null)
    setIsMobileOpen(false)
    setSearchParams({}, { replace: true })
  }

  const onCreated = (id: string) => {
    queryClient.invalidateQueries({ queryKey: ['direct-messages'] })
    select(id)
  }

  const list = (
    <DmListPanel
      conversations={conversations}
      selectedId={selectedId}
      onSelect={select}
      onNew={() => setPickerOpen(true)}
      isLoading={isLoading}
      isError={isError}
      filter={filter}
      onFilterChange={setFilter}
      currentUserId={user?.id}
      callerRole={user?.role}
    />
  )

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-[calc(100vh-3.5rem)]">
        <div className="h-14 bg-white border-b border-gray-200 flex items-center px-4 md:px-6 justify-between shrink-0">
          <h1 className="text-lg font-semibold text-gray-900">Nachrichten</h1>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            title="Aktualisieren"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="hidden md:flex flex-1 min-h-0">
            {list}
            <DmConversationPanel
              conversation={selected as DmConversation | null}
              currentUserId={user?.id}
              onLeft={onLeft}
            />
          </div>
          <div className="md:hidden flex-1 min-h-0">{list}</div>
        </div>
        {isMobileOpen && selected && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/30" onClick={() => setIsMobileOpen(false)} />
            <div className="relative ml-auto w-full bg-gray-50 flex flex-col shadow-xl animate-slide-in">
              <DmConversationPanel
                conversation={selected}
                currentUserId={user?.id}
                onClose={() => setIsMobileOpen(false)}
                onLeft={onLeft}
              />
            </div>
          </div>
        )}
        <NewConversationDialog open={pickerOpen} onClose={() => setPickerOpen(false)} onCreated={onCreated} />
      </div>
    </ErrorBoundary>
  )
}
